import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, renderList as _renderList, isRef as _isRef, withCtx as _withCtx, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "ocr-form" }
const _hoisted_2 = { class: "ocr-form__header" }
const _hoisted_3 = { class: "ocr-form__header__info" }
const _hoisted_4 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "ocr-form__content" }
const _hoisted_7 = { class: "ocr-form__content-container" }
const _hoisted_8 = {
  key: 0,
  class: "ocr-form__skeleton-container"
}
const _hoisted_9 = {
  key: 1,
  class: "ocr-form__preview"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = {
  key: 1,
  class: "ocr-form__preview-image"
}
const _hoisted_12 = ["src"]
const _hoisted_13 = {
  key: 1,
  class: "ocr-form__sections-wrapper"
}
const _hoisted_14 = { class: "ocr-form__sections" }
const _hoisted_15 = { class: "ocr-form__content--small" }
const _hoisted_16 = { class: "ocr-form__content-line-item" }
const _hoisted_17 = { class: "ocr-form__content-line-item" }
const _hoisted_18 = { class: "ocr-form__content-line-item" }
const _hoisted_19 = { class: "ocr-form__content-line-item" }
const _hoisted_20 = {
  key: 0,
  class: "ocr-form__line-mode"
}
const _hoisted_21 = { class: "ocr-form__line-mode-subtitle typographies-paragraph-medium-regular" }
const _hoisted_22 = { class: "typographies-paragraph-medium-regular ocr-form__total" }
const _hoisted_23 = { class: "ocr-form__total__amount-excluding-tax" }
const _hoisted_24 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_25 = { class: "ocr-form__vat-total" }
const _hoisted_26 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_27 = { class: "ocr-form__total-incl-tax" }
const _hoisted_28 = { class: "typographies-paragraph-medium-bold" }
const _hoisted_29 = { class: "ocr-form__content--small" }
const _hoisted_30 = { class: "ocr-form__payment-header" }
const _hoisted_31 = { class: "typographies-heading-h3" }
const _hoisted_32 = { class: "ocr-form__content-line-item" }
const _hoisted_33 = { class: "ocr-form__footer" }
const _hoisted_34 = { class: "ocr-form__footer-actions" }

import {
    computed,
    inject,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
    type ComponentPublicInstance,
    type ComputedRef,
    type Ref,
} from 'vue';
import { useFieldArray } from 'vee-validate';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { cloneDeep, isNil } from 'lodash';

import Heading from 'vcomponents/common/Heading/Heading.vue';
import CurrencyInput from 'vcomponents/inputs/CurrencyInput.vue';
import OcrVerificationProductsTable from 'vcomponents/ocr/OcrVerificationProductsTable.vue';
import OcrVerificationFormLoading from 'vcomponents/ocr/OcrVerificationFormLoading.vue';
import DeleteOcrProductLineModal from 'vcomponents/ocr/DeleteOcrProductLineModal.vue';
import OcrAmountDifferenceModal from 'vcomponents/ocr/OcrAmountDifferenceModal.vue';
import OcrTaxLinesTable from 'vcomponents/ocr/OcrTaxLinesTable.vue';
import SplitPane from 'vcomponents/layout/SplitPane.vue';

import { goToUrl } from 'vueDir/helpers';
import { hydrateOcr } from 'Services/Ocr/Ocr.hydrator';
import { persistOcr } from 'Services/Ocr/Ocr.persistor';
import {
    DEFAULT_AMOUNT_DECIMALS,
    calculateTaxLinesTotalTax,
    calculateTaxLinesTotalWithoutTax,
    calculateTaxLinesTotalWithTax,
    calculateTotalByTax,
    calculateTotalTax,
    calculateTotalWithTax,
    calculateTotalWithoutTax,
    createEmptyProduct,
    createEmptyTaxLine,
    getUniqueTaxRows,
    limitToDecimals,
    ocrTotalDiffersFromLineTotal,
    parseAmountToNumber,
    resolveAmountDecimals,
    setProductDescriptionFromAccountingCode,
} from 'composables/useOcrProducts';
import { useOcrForm } from 'composables/ocr/useOcrForm';
import {
    isPaymentPartiallyFilled,
    type OcrFormValues,
    type OcrPayment,
    type OcrTaxLine,
} from 'composables/ocr/ocrFormSchema';

import Ocr from 'api/ocr';
import Companies from 'api/companies';
import Feature from 'Services/Feature';
import { useOcrVerificationStore } from 'piniaStores/ocr/ocrVerificationStore';

import { useOcrDictionaries } from 'composables/ocr/useOcrDictionaries';
import { useOcrSupplierSearch } from 'composables/ocr/useOcrSupplierSearch';
import { useOcrTags } from 'composables/ocr/useOcrTags';
import { useOcrNavigationQueue } from 'composables/ocr/useOcrNavigationQueue';
import { useDateUtils } from 'composables/useDateUtils';
import {
    OcrFormStep,
    OcrFormType,
    OcrLineMode,
    type AnalyticsMode,
    type OcrPreview,
    type OcrProduct,
    type OcrStepOption,
    type OcrTypeOption,
} from 'composables/ocr/types';
import type { ErrorsHandler, RootState } from 'types/store/rootState.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Currency } from 'types/entities/system/currency.type';
import type { FormTag } from 'types/entities/smartTags/tag.type';
import type { PresetSupplier } from 'types/entities/company/third.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';
import { ThirdEvent, type ThirdCreatedPayload } from 'types/events/third.events';

// Props
interface Props {
    canCreateSupplier: boolean;
    linkedtype: string;
    linkedid: number;
}
const previewInitialWidth = 640;
const minPreviewWidth = 360;
const minFormWidth = 520;
const splitterWidth = 12;
const urlBackToList = '/ocr/list/purInvoice';

const ERROR_CODE_TOO_MANY_PAGES = 1;

// Refs

export default /*@__PURE__*/_defineComponent({
  __name: 'OcrVerificationForm',
  props: {
    canCreateSupplier: { type: Boolean },
    linkedtype: {},
    linkedid: {}
  },
  setup(__props: any) {

const props = __props;

// Injections
const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

// Router & store
const router = useRouter();
const store = useStore();
const ocrVerificationStore = useOcrVerificationStore();

// Static layout constants
const documentSectionRef = ref<ComponentPublicInstance | null>(null);
const productsSectionRef = ref<ComponentPublicInstance | null>(null);
const paymentsSectionRef = ref<ComponentPublicInstance | null>(null);
const loading = ref(true);
const processing = ref(false);
const submitting = ref(false);
const showNote = ref(false);
const preview = ref<OcrPreview>({ mime: null, url: null });
const isElectronic = ref(false);
const analyticsMode = ref<AnalyticsMode>(null);
const currencySymbol = ref<string | null>(null);
const errorCode = ref<number | null>(null);
const ocrId = ref<string | null>(null);
const defaultAccountingCodeCompany = ref<number | null>(null);
const productDeleteModalVisible = ref(false);
const productDeleteIndex = ref<number | null>(null);
const productDeleteRememberChoice = ref(false);
const amountDifferenceModalVisible = ref(false);
const pendingSubmitValues = ref<OcrFormValues | null>(null);

const isOcrFormType = (value: string): value is OcrFormType =>
    Object.values<string>(OcrFormType).includes(value);

const initialType: OcrFormType = isOcrFormType(props.linkedtype)
    ? props.linkedtype
    : OcrFormType.INVOICE;
const initialStep = initialType === OcrFormType.CREDIT_NOTE ? OcrFormStep.STORED : OcrFormStep.DUE;

const initialFormValues = {
    type: initialType,
    supplier: null,
    documentDate: '',
    dueDate: '',
    externalDocumentName: '',
    analyticCode: '',
    currency: null,
    step: initialStep,
    note: '',
    products: [createEmptyProduct()],
    lineMode: ocrVerificationStore.lineMode,
    taxLines: [createEmptyTaxLine()],
    payments: [],
    tags: [],
    totalWithoutTax: null,
    totalWithTax: null,
} satisfies OcrFormValues;

const ocrForm = useOcrForm({ initialValues: initialFormValues });
const {
    values: form,
    errors: rawErrors,
    submitCount,
    handleSubmit,
    resetForm,
    productsArray,
    paymentsArray,
} = ocrForm;

const taxLinesArray = useFieldArray<OcrTaxLine>('taxLines');

// Errors are only displayed once the user has attempted to submit at least once.
const errors = computed<Partial<Record<string, string>>>(() =>
    submitCount.value > 0 ? rawErrors.value : {},
);

const sectionRefByField: Record<string, Ref<ComponentPublicInstance | null>> = {
    type: documentSectionRef,
    step: documentSectionRef,
    supplier: documentSectionRef,
    documentDate: documentSectionRef,
    dueDate: documentSectionRef,
    currency: documentSectionRef,
    externalDocumentName: documentSectionRef,
    analyticCode: documentSectionRef,
    note: documentSectionRef,
    tags: documentSectionRef,
    products: productsSectionRef,
    payments: paymentsSectionRef,
};

const scrollToFirstError = async (): Promise<void> => {
    await nextTick();
    const firstErrorPath = Object.keys(errors.value)[0];
    if (!firstErrorPath) {
        return;
    }
    const fieldName = firstErrorPath.split(/[.[]/)[0];
    const sectionEl = sectionRefByField[fieldName]?.value?.$el;
    if (sectionEl instanceof HTMLElement) {
        sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

// Writable model refs for top-level fields used with v-model.
// vee-validate's `values` is read-only; `defineField` returns a writable ref.
const [typeModel] = ocrForm.defineField('type');
const [stepModel] = ocrForm.defineField('step');
const [externalDocumentNameModel] = ocrForm.defineField('externalDocumentName');
const [analyticCodeModel] = ocrForm.defineField('analyticCode');
const [noteModel] = ocrForm.defineField('note');

const updatePaymentField = <K extends keyof OcrPayment>(
    index: number,
    field: K,
    value: OcrPayment[K],
): void => {
    const current = paymentsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    paymentsArray.update(index, { ...current, [field]: value });
};

const formatPaymentAmountOnBlur = (index: number): void => {
    const current = paymentsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const value = current.amount;
    if (value === null || value === '') {
        return;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isNaN(parsed)) {
        updatePaymentField(
            index,
            'amount',
            parsed.toFixed(DEFAULT_AMOUNT_DECIMALS).replace('.', ','),
        );
    }
};

// Vuex bridges (legacy stores not yet migrated to Pinia)
const rootState = computed<RootState>(() => store.state as RootState);
const units = computed<Unit[]>(() => rootState.value.corp.units);
const uiLang = computed<string>(() => rootState.value.corp.uiLang);

const companyAmountDecimals = computed<number>(() => {
    const precision = Number(rootState.value.corp.numformat?.precision);
    return Number.isInteger(precision) && precision >= 0 ? precision : DEFAULT_AMOUNT_DECIMALS;
});
const amountDecimals = computed<number>(() =>
    resolveAmountDecimals(isElectronic.value, companyAmountDecimals.value),
);
const allCurrencies = computed<Currency[]>(() => rootState.value.system.currencies);
const errorsHandler = computed<ErrorsHandler>(() => rootState.value.system.errorsHandler);
const { permissions } = storeToRefs(ocrVerificationStore);

// Dictionaries
const {
    accountingCodes,
    taxRates,
    paymentTypes,
    warehouses,
    defaultAccountingCode,
    presetSupplier,
    defaultTags,
    loadDictionaries,
} = useOcrDictionaries();

// Navigation queue
const { multipleVerify, totalRows, completeRows, goNextOcr, clearQueue, rowsExcluding } =
    useOcrNavigationQueue();

// Derived references for composables
const isCreditNote = computed<boolean>(() => form.type === OcrFormType.CREDIT_NOTE);
const supplierIdRef = computed<number | null>(() => form.supplier);
const canCreateSupplierRef = computed<boolean>(() => props.canCreateSupplier);
const formTagsRef = computed<FormTag[]>({
    get: () => form.tags,
    set: (value: FormTag[]) => {
        ocrForm.setFieldValue<'tags'>('tags', value);
    },
});

// Supplier search
const {
    supplierResults,
    supplierLoading,
    supplierActions,
    supplierSelectKey,
    remoteSupplier,
    supplierDefaultValues,
    onSupplierSearch,
    onSupplierFocus,
    bumpSupplierSelectKey,
} = useOcrSupplierSearch({
    presetSupplier,
    currentSupplierId: supplierIdRef,
    canCreateSupplier: canCreateSupplierRef,
});

// Tags
const { tagsResults, tagsLoading, tagDefaultValues, onTagsSearch, tagsUpdated } = useOcrTags({
    defaultTags,
    formTags: formTagsRef,
});

const { formatDateDisplayed } = useDateUtils();

// Computed
const isStockEnabled = computed<boolean>(() => Feature.isEnabled('ocr-stock'));
const isLineItemsEnabled = computed<boolean>(
    () => Feature.isEnabled('ocr-line-items') && Feature.isEnabled('ocr-mistral'),
);
const canCreateInvoice = computed<boolean>(() => Boolean(permissions.value['create_pur_invoice']));
const canCreateCreditNote = computed<boolean>(() =>
    Boolean(permissions.value['create_pur_credit_note']),
);
const canAddPayment = computed<boolean>(() => Boolean(permissions.value['create_payment']));

const availableType = computed<OcrTypeOption[]>(() =>
    [
        {
            label: $t.__('common.labels.invoice'),
            value: OcrFormType.INVOICE,
            enabled: canCreateInvoice.value,
        },
        {
            label: $t.__('common.labels.creditnote'),
            value: OcrFormType.CREDIT_NOTE,
            enabled: canCreateCreditNote.value,
        },
    ].filter((type) => type.enabled !== false),
);

const availableStep = computed<OcrStepOption[]>(() => {
    if (isCreditNote.value) {
        return [
            {
                label: $t.__('ocr.OcrVerificationForm.step_draft'),
                value: OcrFormStep.DRAFT,
                color: 'purple',
            },
            {
                label: $t.__('ocr.OcrVerificationForm.step_stored'),
                value: OcrFormStep.STORED,
                color: 'yellow',
            },
        ];
    }
    const steps = [
        {
            label: $t.__('ocr.OcrVerificationForm.step_draft'),
            value: OcrFormStep.DRAFT,
            color: 'purple',
        },
        {
            label: $t.__('ocr.OcrVerificationForm.step_due'),
            value: OcrFormStep.DUE,
            color: 'yellow',
        },
    ];
    return isElectronic.value ? steps.filter((step) => step.value !== OcrFormStep.DRAFT) : steps;
});

const defaultTaxRateId = computed<number | null>(() =>
    taxRates.value.length > 0 ? taxRates.value[0].id : null,
);

const lineMode = computed<OcrLineMode>({
    get: () => form.lineMode,
    set: (value) => {
        ocrForm.setFieldValue<'lineMode'>('lineMode', value);
        ocrVerificationStore.setLineMode(value);
    },
});

const isTaxMode = computed<boolean>(() => lineMode.value === OcrLineMode.TAX);

const showLineModeToggle = computed<boolean>(() => !isElectronic.value);

const lineModeOptions: { value: OcrLineMode; label: string }[] = [
    { value: OcrLineMode.PRODUCT, label: $t.__('ocr.OcrVerificationForm.line_mode_product') },
    { value: OcrLineMode.TAX, label: $t.__('ocr.OcrVerificationForm.line_mode_tax') },
];

const totalWithoutTax = computed<number>(() =>
    isTaxMode.value
        ? calculateTaxLinesTotalWithoutTax(form.taxLines)
        : calculateTotalWithoutTax(form.products, amountDecimals.value),
);

const taxRows: ComputedRef<TaxRate[]> = computed(() =>
    getUniqueTaxRows(form.products, taxRates.value),
);

const totalTax = computed<number>(() =>
    isTaxMode.value
        ? calculateTaxLinesTotalTax(form.taxLines)
        : calculateTotalTax(form.products, taxRows.value, amountDecimals.value),
);

const totalWithTax = computed<number>(() =>
    isTaxMode.value
        ? calculateTaxLinesTotalWithTax(form.taxLines)
        : calculateTotalWithTax(totalWithoutTax.value, totalTax.value, amountDecimals.value),
);

// Total incl. tax read by the OCR from the document, used to warn about a
// discrepancy with the total computed from the line items on validation.
const ocrTotalWithTax = computed<number>(() => parseAmountToNumber(form.totalWithTax));

const hasTotalDiscrepancy = computed<boolean>(() =>
    ocrTotalDiffersFromLineTotal(form.totalWithTax, totalWithTax.value, amountDecimals.value),
);

const documentTypeLabel = computed<string>(() => {
    if (isElectronic.value) {
        return isCreditNote.value
            ? $t.__('ocr.OcrVerificationForm.type_electronic_credit_note')
            : $t.__('ocr.OcrVerificationForm.type_electronic_invoice');
    }
    return isCreditNote.value
        ? $t.__('ocr.OcrVerificationForm.type_imported_credit_note')
        : $t.__('ocr.OcrVerificationForm.type_imported_invoice');
});

const errorLabel = computed<string>(() => {
    const errorMessage =
        errorCode.value === 1
            ? $t.__('ocr.OcrVerificationForm.error_too_many_pages')
            : $t.__('ocr.OcrVerificationForm.error_technical');
    const manualEntryMessage = $t.__('ocr.OcrVerificationForm.error_manual_entry');
    return `${errorMessage} ${manualEntryMessage}`;
});

// Methods
const formatAmount = (value: number | null | undefined): string => {
    const options: Intl.NumberFormatOptions = form.currency
        ? {
              minimumFractionDigits: amountDecimals.value,
              maximumFractionDigits: amountDecimals.value,
              style: 'currency',
              currency: form.currency,
          }
        : {
              minimumFractionDigits: amountDecimals.value,
              maximumFractionDigits: amountDecimals.value,
              style: 'decimal',
          };
    return new Intl.NumberFormat(uiLang.value, options).format(value ?? 0);
};

const getTotalByTax = (taxId: number, taxRate: string): number =>
    calculateTotalByTax(form.products, taxId, taxRate, amountDecimals.value);

const getPaymentError = (index: number, field: 'date' | 'amount' | 'payment_mean'): string => {
    return errors.value[`payments[${index}].${field}`] ?? '';
};

const updateAccountingCodeProduct = async (supplierId: number): Promise<void> => {
    const response = await Companies.getCompanyById(supplierId, {
        field: ['accounting_purchase_code_id'],
    });
    const data = response.data as { accounting_purchase_code_id: number | null };
    defaultAccountingCodeCompany.value =
        data.accounting_purchase_code_id === null
            ? defaultAccountingCode.value
            : data.accounting_purchase_code_id;
    for (const [index, field] of productsArray.fields.value.entries()) {
        if (!field.value.catalogueLink) {
            const next: OcrProduct = {
                ...field.value,
                accountingCode: defaultAccountingCodeCompany.value,
            };
            setProductDescriptionFromAccountingCode(next, accountingCodes.value);
            productsArray.update(index, next);
        }
    }
};

const updateSupplier = (supplierId: number): void => {
    void updateAccountingCodeProduct(supplierId);
    ocrForm.setFieldValue<'supplier'>('supplier', supplierId);
};

const onSupplierChange = (selected: { value: number } | null): void => {
    if (!selected) {
        ocrForm.setFieldValue<'supplier'>('supplier', null);
        bumpSupplierSelectKey();
        return;
    }
    updateSupplier(selected.value);
};

const getSupplier = async (id: number): Promise<void> => {
    processing.value = true;
    const response = await Companies.getCompanyById(id, { field: ['id', 'name'] });
    processing.value = false;
    const supplierData = response.data as PresetSupplier;
    ocrForm.setFieldValue<'supplier'>('supplier', supplierData.id);
    presetSupplier.value.push(supplierData);
    bumpSupplierSelectKey();
};

const addProduct = (product?: OcrProduct): void => {
    const newProduct = product
        ? cloneDeep(product)
        : createEmptyProduct({
              defaultAccountingCode: defaultAccountingCode.value,
              defaultTaxRateId: defaultTaxRateId.value,
              supplierId: form.supplier,
              companyAccountingCode: defaultAccountingCodeCompany.value,
          });
    productsArray.push(newProduct);
};

const addTaxLine = (): void => {
    taxLinesArray.push(
        createEmptyTaxLine({
            defaultAccountingCode: defaultAccountingCode.value,
            defaultTaxRateId: defaultTaxRateId.value,
        }),
    );
};

const requestTaxLineDelete = (index: number): void => {
    if (taxLinesArray.fields.value.length > 1) {
        taxLinesArray.remove(index);
    }
};

const addPayment = (): void => {
    paymentsArray.push({ date: '', reference: '', amount: null, payment_mean: '' });
};

const deletePayment = (index: number): void => {
    paymentsArray.remove(index);
};

const removeProduct = (index: number): void => {
    productsArray.remove(index);
};

const requestProductDelete = (index: number): void => {
    if (productDeleteRememberChoice.value) {
        removeProduct(index);
        return;
    }
    productDeleteIndex.value = index;
    productDeleteModalVisible.value = true;
};

const closeProductDeleteModal = (): void => {
    productDeleteModalVisible.value = false;
    productDeleteIndex.value = null;
};

const onProductDeleteConfirm = (): void => {
    if (productDeleteIndex.value !== null) {
        removeProduct(productDeleteIndex.value);
    }
    closeProductDeleteModal();
};

const onProductDeleteCancel = (): void => {
    closeProductDeleteModal();
};

const updateCurrency = (currency: Currency): void => {
    ocrForm.setFieldValue<'currency'>('currency', currency.name);
    currencySymbol.value = currency.symbol ?? null;
};

const updateDate = (date: string): void => {
    ocrForm.setFieldValue<'documentDate'>('documentDate', date);
};

const updateDueDate = (date: string): void => {
    ocrForm.setFieldValue<'dueDate'>('dueDate', date);
};

const cancel = (): void => {
    clearQueue();
    goToUrl(urlBackToList);
};

const skip = (): void => {
    processing.value = true;
    if (multipleVerify.value) {
        const remaining = rowsExcluding(ocrId.value);
        if (remaining.length) {
            goNextOcr(remaining, 0);
            return;
        }
    }
    clearQueue();
    goToUrl(urlBackToList);
};

const deleteOCR = async (): Promise<void> => {
    processing.value = true;
    await Ocr.deleteOcr(ocrId.value);
    processing.value = false;
    const remaining = rowsExcluding(ocrId.value);
    if (remaining.length && multipleVerify.value) {
        goNextOcr(remaining, 0);
        return;
    }
    clearQueue();
    goToUrl(urlBackToList);
};

const performSubmit = async (validatedValues: OcrFormValues): Promise<void> => {
    if (submitting.value) {
        return;
    }
    submitting.value = true;

    const formData = persistOcr(
        {
            ...validatedValues,
            totalWithoutTax: totalWithoutTax.value,
            totalWithTax: totalWithTax.value,
        },
        {
            currencies: allCurrencies.value,
            taxRates: taxRates.value,
            decimals: amountDecimals.value,
        },
    );

    try {
        const response = (await Ocr.submitVerification(
            ocrId.value,
            validatedValues.type,
            formData,
        )) as { docid: number };
        const remaining = rowsExcluding(ocrId.value);
        if (remaining.length && multipleVerify.value) {
            goNextOcr(remaining, 0);
            return;
        }

        const isMultiple = multipleVerify.value;
        clearQueue();

        const documentKind = isCreditNote.value ? 'credit_note' : 'invoice';
        const cardinality = isMultiple ? 'multiple' : 'single';
        const messageKey = `ocr.OcrVerificationForm.success_${documentKind}_${cardinality}`;

        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            icon: true,
            text: $t.__(messageKey),
            duration: 5000,
        });

        if (isMultiple) {
            const listRoute = isCreditNote.value ? 'purchasecreditnotes' : 'purchaseinvoices';
            router.push({ name: listRoute });
        } else {
            const overviewRoute = isCreditNote.value
                ? 'purchases_purCreditNoteOverview'
                : 'purchases_purInvoiceOverview';
            goToUrl(`/?_f=${overviewRoute}&id=${response.docid}`);
        }
    } catch (error) {
        errorsHandler.value.logException(error as Error);
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('ocr.OcrVerificationForm.error_generic'),
            duration: 5000,
        });
        submitting.value = false;
    }
};

const submit = handleSubmit(
    async (validatedValues) => {
        if (submitting.value) {
            return;
        }
        if (hasTotalDiscrepancy.value) {
            pendingSubmitValues.value = validatedValues;
            amountDifferenceModalVisible.value = true;
            return;
        }
        await performSubmit(validatedValues);
    },
    () => {
        void scrollToFirstError();
    },
);

const confirmAmountDifference = (): void => {
    amountDifferenceModalVisible.value = false;
    const values = pendingSubmitValues.value;
    pendingSubmitValues.value = null;
    if (values) {
        void performSubmit(values);
    }
};

const cancelAmountDifference = (): void => {
    amountDifferenceModalVisible.value = false;
    pendingSubmitValues.value = null;
};

const getOcrById = async (): Promise<void> => {
    try {
        const response = await Ocr.getOcrById(ocrId.value);
        if (response.state === 'error') {
            errorCode.value = response.error_code;
            if (response.error_code === ERROR_CODE_TOO_MANY_PAGES) {
                lineMode.value = OcrLineMode.TAX;
            }
        } else {
            isElectronic.value = response.is_einvoicing ?? false;
            const { formData, analyticsMode: hydratedAnalyticsMode } = hydrateOcr(
                response.response,
                {
                    taxList: taxRates.value,
                    accountingCodes: accountingCodes.value,
                    defaultAccountingCode: defaultAccountingCode.value,
                    paymentTypes: paymentTypes.value,
                    decimals: amountDecimals.value,
                },
            ) as { formData: Partial<OcrFormValues>; analyticsMode: AnalyticsMode };
            analyticsMode.value = hydratedAnalyticsMode;
            resetForm({ values: { ...initialFormValues, ...formData } });
            ocrForm.setFieldValue<'lineMode'>('lineMode', ocrVerificationStore.lineMode);
            if (response.page_limit_exceeded) {
                lineMode.value = OcrLineMode.TAX;
            }
            if (response.linked_type) {
                ocrForm.setFieldValue<'type'>('type', response.linked_type);
            }
            currencySymbol.value =
                allCurrencies.value.find((currency) => form.currency === currency.name)?.symbol ??
                null;
            const supplierData = response.response.supplier;
            if (supplierData?.id !== null && supplierData?.value !== null) {
                const alreadyExists = presetSupplier.value.some(
                    (supplier) => supplier.id === supplierData.id,
                );
                if (!alreadyExists) {
                    presetSupplier.value.push({
                        id: parseInt(supplierData.id, 10),
                        name: supplierData.value,
                    });
                }
            } else if (supplierData?.value !== null) {
                remoteSupplier.value = supplierData.value;
            }
            if (!isNil(form.supplier)) {
                await updateAccountingCodeProduct(form.supplier);
            }
        }
        if (!isNil(response.preview)) {
            preview.value = response.preview;
        }
        loading.value = false;
    } catch (error) {
        router.push({
            name: 'error.not.found',
            params: {
                title: $t.__('ocr.OcrVerificationForm.document_not_found'),
                buttonOptions: {
                    label: $t.__('ocr.OcrVerificationForm.back_to_list'),
                    url: urlBackToList,
                },
            },
        } as never);
        loading.value = false;
        errorsHandler.value.logException(error as Error);
    }
};

// Watchers
watch(
    () => form.type,
    () => {
        const validSteps = availableStep.value.map((step) => step.value);
        if (!validSteps.includes(form.step)) {
            ocrForm.setFieldValue<'step'>('step', validSteps[1] ?? validSteps[0]);
        }
    },
);

// Init (equivalent to Options API `created`)
ocrId.value = props.linkedid ? String(props.linkedid) : null;

void ocrVerificationStore.loadMeta();

const onThirdCreated = (payload: ThirdCreatedPayload): void => {
    void getSupplier(payload.thirdid);
};
EventBus.on(ThirdEvent.CREATED, onThirdCreated);

void loadDictionaries().then(() => getOcrById());

onBeforeUnmount(() => {
    EventBus.off(ThirdEvent.CREATED, onThirdCreated);
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!
  const _component_CompassLoader = _resolveComponent("CompassLoader")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassTag = _resolveComponent("CompassTag")!
  const _component_CompassAutoComplete = _resolveComponent("CompassAutoComplete")!
  const _component_CompassDatePicker = _resolveComponent("CompassDatePicker")!
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassAccordion = _resolveComponent("CompassAccordion")!
  const _component_CompassSegmentedControl = _resolveComponent("CompassSegmentedControl")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Heading, {
        labelTitle: _unref($t).__('ocr.OcrVerificationForm.verification_space'),
        showBackButton: "",
        onGoBack: cancel
      }, null, 8 /* PROPS */, ["labelTitle"]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, _toDisplayString(documentTypeLabel.value), 1 /* TEXT */),
        (_unref(multipleVerify))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _cache[10] || (_cache[10] = _createElementVNode("span", { class: "ocr-form__header-remaining" }, " | ", -1 /* CACHED */)),
              _createCommentVNode(" eslint-disable vue/no-v-html "),
              _createElementVNode("span", {
                class: "ocr-form__header-remaining",
                innerHTML: 
                            _unref($t).__('ocr.OcrVerificationForm.label_remaining', {
                                current: _unref(completeRows) + 1,
                                total: _unref(totalRows),
                            })
                        
              }, null, 8 /* PROPS */, _hoisted_5),
              _createCommentVNode(" eslint-enable vue/no-v-html ")
            ], 64 /* STABLE_FRAGMENT */))
          : _createCommentVNode("v-if", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createVNode(SplitPane, {
          class: "split-pane-container",
          initialLeft: previewInitialWidth,
          leftMin: minPreviewWidth,
          rightMin: minFormWidth,
          splitterWidth: splitterWidth,
          storageKey: "ocr-verification-split"
        }, {
          left: _withCtx(() => [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_component_CompassSkeleton, {
                    class: "ocr-form__skeleton",
                    variant: _ctx.CompassEnums.SkeletonVariantType.BLOCK
                  }, null, 8 /* PROPS */, ["variant"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  (preview.value.mime === 'application/pdf')
                    ? (_openBlock(), _createElementBlock("embed", {
                        key: 0,
                        src: preview.value.url ?? undefined,
                        type: "application/pdf"
                      }, null, 8 /* PROPS */, _hoisted_10))
                    : (preview.value.url)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _createElementVNode("img", {
                            src: preview.value.url ?? undefined
                          }, null, 8 /* PROPS */, _hoisted_12)
                        ]))
                      : _createCommentVNode("v-if", true)
                ]))
          ]),
          right: _withCtx(() => [
            (loading.value)
              ? (_openBlock(), _createBlock(OcrVerificationFormLoading, { key: 0 }))
              : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  (errorCode.value)
                    ? (_openBlock(), _createBlock(_component_CompassAlert, {
                        key: 0,
                        type: _ctx.CompassEnums.AlertType.ERROR,
                        description: errorLabel.value
                      }, null, 8 /* PROPS */, ["type", "description"]))
                    : _createCommentVNode("v-if", true),
                  (processing.value)
                    ? (_openBlock(), _createBlock(_component_CompassLoader, { key: 1 }))
                    : _createCommentVNode("v-if", true),
                  _withDirectives(_createElementVNode("div", _hoisted_14, [
                    _createCommentVNode(" Document "),
                    _createVNode(_component_CompassAccordion, {
                      ref_key: "documentSectionRef",
                      ref: documentSectionRef,
                      class: "ocr-form__card-section ocr-form__content-document",
                      title: _unref($t).__('ocr.OcrVerificationForm.document_section'),
                      type: _ctx.CompassEnums.AccordionType.FUNCTIONAL,
                      coloredIconProps: { icon: 'document', color: 'teal' },
                      open: ""
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_15, [
                          _createElementVNode("div", _hoisted_16, [
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('common.labels.type'),
                              required: "",
                              error: errors.value.type
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassSelect, {
                                  modelValue: _unref(typeModel),
                                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(typeModel) ? (typeModel).value = $event : null)),
                                  disabled: submitting.value || isElectronic.value,
                                  error: !!errors.value.type,
                                  menuClass: "ocr-form__select-fit-dropdown"
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableType.value, (type) => {
                                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                                        key: type.value,
                                        value: type.value,
                                        label: type.label
                                      }, null, 8 /* PROPS */, ["value", "label"]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["modelValue", "disabled", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"]),
                            _createVNode(_component_CompassFormItem, {
                              label: 
                                                    _unref($t).__('ocr.OcrVerificationForm.document_status')
                                                ,
                              required: "",
                              error: errors.value.step
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassSelect, {
                                  modelValue: _unref(stepModel),
                                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(stepModel) ? (stepModel).value = $event : null)),
                                  disabled: submitting.value,
                                  type: _ctx.CompassEnums.SelectType.TAG,
                                  error: !!errors.value.step,
                                  menuClass: "ocr-form__select-fit-dropdown"
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableStep.value, (step) => {
                                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                                        key: step.value,
                                        value: step.value,
                                        label: step.label,
                                        color: step.color
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_CompassTag, {
                                            color: step.color,
                                            size: _ctx.CompassEnums.TagSize.SMALL
                                          }, {
                                            default: _withCtx(() => [
                                              _createTextVNode(_toDisplayString(step.label), 1 /* TEXT */)
                                            ]),
                                            _: 2 /* DYNAMIC */
                                          }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["color", "size"])
                                        ]),
                                        _: 2 /* DYNAMIC */
                                      }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["value", "label", "color"]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["modelValue", "disabled", "type", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"])
                          ]),
                          _createElementVNode("div", _hoisted_17, [
                            _createVNode(_component_CompassFormItem, {
                              label: 
                                                    _unref($t).__('ocr.OcrVerificationForm.supplier_name')
                                                ,
                              required: "",
                              error: errors.value.supplier,
                              class: "ocr-form__form-item--flex"
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(), _createBlock(_component_CompassAutoComplete, {
                                  key: _unref(supplierSelectKey),
                                  ref: "supplierSelect",
                                  results: _unref(supplierResults),
                                  defaultValues: _unref(supplierDefaultValues),
                                  disabled: submitting.value,
                                  multiple: false,
                                  multipleLimit: 1,
                                  triggerAfter: 0,
                                  menuClass: "ocr-form__select-fit-dropdown",
                                  placeholder: 
                                                        _unref($t).__(
                                                            'ocr.OcrVerificationForm.enter_supplier_name',
                                                        )
                                                    ,
                                  noResultsLabel: 
                                                        _unref($t).__(
                                                            'ocr.OcrVerificationForm.no_supplier_found',
                                                        )
                                                    ,
                                  loading: _unref(supplierLoading),
                                  actions: _unref(supplierActions),
                                  error: !!errors.value.supplier,
                                  "onUpdate:modelValue": onSupplierChange,
                                  onUpdateSearch: _unref(onSupplierSearch),
                                  onFocus: _unref(onSupplierFocus)
                                }, null, 8 /* PROPS */, ["results", "defaultValues", "disabled", "placeholder", "noResultsLabel", "loading", "actions", "error", "onUpdateSearch", "onFocus"]))
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"]),
                            _createVNode(_component_CompassFormItem, {
                              label: 
                                                    _unref($t).__('ocr.OcrVerificationForm.document_date')
                                                ,
                              required: "",
                              error: errors.value.documentDate,
                              class: "ocr-form__form-item--date"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassDatePicker, {
                                  modelValue: _unref(form).documentDate,
                                  disabled: submitting.value || isElectronic.value,
                                  formatDateDisplayed: _unref(formatDateDisplayed),
                                  error: !!errors.value.documentDate,
                                  "onUpdate:modelValue": updateDate
                                }, null, 8 /* PROPS */, ["modelValue", "disabled", "formatDateDisplayed", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"]),
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('common.labels.expected_close_date'),
                              required: !isCreditNote.value,
                              error: errors.value.dueDate,
                              class: "ocr-form__form-item--date"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassDatePicker, {
                                  modelValue: _unref(form).dueDate,
                                  disabled: submitting.value || isElectronic.value,
                                  formatDateDisplayed: _unref(formatDateDisplayed),
                                  error: !!errors.value.dueDate,
                                  "onUpdate:modelValue": updateDueDate
                                }, null, 8 /* PROPS */, ["modelValue", "disabled", "formatDateDisplayed", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "required", "error"])
                          ]),
                          _createElementVNode("div", _hoisted_18, [
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('common.labels.currency'),
                              required: "",
                              error: errors.value.currency,
                              class: "ocr-form__form-item--currency"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(CurrencyInput, {
                                  modelValue: _unref(form).currency ?? '',
                                  disabled: submitting.value || isElectronic.value,
                                  mode: "symbol",
                                  error: !!errors.value.currency,
                                  "onUpdate:modelValue": updateCurrency
                                }, null, 8 /* PROPS */, ["modelValue", "disabled", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"]),
                            _createVNode(_component_CompassFormItem, {
                              label: 
                                                    _unref($t).__(
                                                        'ocr.OcrVerificationForm.external_supplier_document_number',
                                                    )
                                                ,
                              class: "ocr-form__form-item--document-number",
                              error: errors.value.externalDocumentName
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassInput, {
                                  modelValue: _unref(externalDocumentNameModel),
                                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(externalDocumentNameModel) ? (externalDocumentNameModel).value = $event : null)),
                                  disabled: submitting.value || isElectronic.value,
                                  error: !!errors.value.externalDocumentName
                                }, null, 8 /* PROPS */, ["modelValue", "disabled", "error"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label", "error"]),
                            (analyticsMode.value === 'doc')
                              ? (_openBlock(), _createBlock(_component_CompassFormItem, {
                                  key: 0,
                                  label: 
                                                    _unref($t).__(
                                                        'document.docFormSettings.TabGeneralSettings.analytic_code_doc',
                                                    )
                                                ,
                                  class: "ocr-form__form-item--analytic-code"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_CompassInput, {
                                      modelValue: _unref(analyticCodeModel),
                                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(analyticCodeModel) ? (analyticCodeModel).value = $event : null)),
                                      disabled: submitting.value
                                    }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["label"]))
                              : _createCommentVNode("v-if", true)
                          ]),
                          _createElementVNode("div", _hoisted_19, [
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('common.labels.smart_tags'),
                              class: "ocr-form__form-item--full-width"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassAutoComplete, {
                                  results: _unref(tagsResults),
                                  defaultValues: _unref(tagDefaultValues),
                                  disabled: submitting.value,
                                  multiple: "",
                                  allowCreate: "",
                                  triggerAfter: 1,
                                  placeholder: 
                                                        _unref($t).__('utilities.Tags.enter_smarttag')
                                                    ,
                                  noResultsLabel: 
                                                        _unref($t).__('common.labels.no_result')
                                                    ,
                                  loading: _unref(tagsLoading),
                                  "onUpdate:modelValue": _unref(tagsUpdated),
                                  onUpdateSearch: _unref(onTagsSearch)
                                }, null, 8 /* PROPS */, ["results", "defaultValues", "disabled", "placeholder", "noResultsLabel", "loading", "onUpdate:modelValue", "onUpdateSearch"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label"])
                          ]),
                          (!showNote.value)
                            ? (_openBlock(), _createBlock(_component_CompassButton, {
                                key: 0,
                                label: _unref($t).__('ocr.OcrVerificationForm.add_note'),
                                type: _ctx.CompassEnums.ButtonType.TERTIARY,
                                disabled: submitting.value,
                                leftIcon: "plus",
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (showNote.value = true))
                              }, null, 8 /* PROPS */, ["label", "type", "disabled"]))
                            : (_openBlock(), _createBlock(_component_CompassFormItem, {
                                key: 1,
                                label: _unref($t).__('common.labels.note'),
                                class: "ocr-form__form-item--full-width"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_CompassInput, {
                                    modelValue: _unref(noteModel),
                                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(noteModel) ? (noteModel).value = $event : null)),
                                    class: "ocr-form__note",
                                    disabled: submitting.value,
                                    type: _ctx.CompassEnums.InputType.TEXTAREA,
                                    rows: 6
                                  }, null, 8 /* PROPS */, ["modelValue", "disabled", "type"])
                                ]),
                                _: 1 /* STABLE */
                              }, 8 /* PROPS */, ["label"]))
                        ])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["title", "type"]),
                    _createVNode(_component_CompassAccordion, {
                      ref_key: "productsSectionRef",
                      ref: productsSectionRef,
                      class: "ocr-form__card-section ocr-form__content-products",
                      title: _unref($t).__('ocr.OcrVerificationForm.products_services'),
                      type: _ctx.CompassEnums.AccordionType.FUNCTIONAL,
                      coloredIconProps: { icon: 'modules', color: 'purple' },
                      open: ""
                    }, {
                      default: _withCtx(() => [
                        (showLineModeToggle.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                              _createElementVNode("p", _hoisted_21, _toDisplayString(_unref($t).__('ocr.OcrVerificationForm.line_mode_hint')), 1 /* TEXT */),
                              _createVNode(_component_CompassSegmentedControl, {
                                modelValue: lineMode.value,
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((lineMode).value = $event)),
                                options: lineModeOptions
                              }, null, 8 /* PROPS */, ["modelValue"])
                            ]))
                          : _createCommentVNode("v-if", true),
                        _withDirectives(_createVNode(OcrVerificationProductsTable, {
                          class: "ocr-form__products-table",
                          disabled: submitting.value,
                          isElectronic: isElectronic.value,
                          decimals: amountDecimals.value,
                          units: units.value,
                          accountingCodes: _unref(accountingCodes),
                          taxRates: _unref(taxRates),
                          warehouses: _unref(warehouses),
                          isStockEnabled: isStockEnabled.value,
                          isLineItemsEnabled: isLineItemsEnabled.value,
                          showAnalyticCode: analyticsMode.value === 'item',
                          currencySymbol: currencySymbol.value ?? undefined,
                          formatDateDisplayed: _unref(formatDateDisplayed),
                          hint: _unref($t).__('ocr.OcrVerificationProductsTable.hint'),
                          addProduct: addProduct,
                          requestProductDelete: requestProductDelete
                        }, null, 8 /* PROPS */, ["disabled", "isElectronic", "decimals", "units", "accountingCodes", "taxRates", "warehouses", "isStockEnabled", "isLineItemsEnabled", "showAnalyticCode", "currencySymbol", "formatDateDisplayed", "hint"]), [
                          [_vShow, !isTaxMode.value]
                        ]),
                        _withDirectives(_createVNode(OcrTaxLinesTable, {
                          class: "ocr-form__products-table",
                          disabled: submitting.value,
                          isElectronic: isElectronic.value,
                          units: units.value,
                          accountingCodes: _unref(accountingCodes),
                          taxRates: _unref(taxRates),
                          showAnalyticCode: analyticsMode.value === 'item',
                          currencySymbol: currencySymbol.value ?? undefined,
                          addTaxLine: addTaxLine,
                          requestTaxLineDelete: requestTaxLineDelete
                        }, null, 8 /* PROPS */, ["disabled", "isElectronic", "units", "accountingCodes", "taxRates", "showAnalyticCode", "currencySymbol"]), [
                          [_vShow, isTaxMode.value]
                        ]),
                        _createElementVNode("div", _hoisted_22, [
                          _createElementVNode("div", _hoisted_23, [
                            _createElementVNode("span", null, _toDisplayString(_unref($t).__('common.labels.amount_excluding_tax')), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_24, _toDisplayString(formatAmount(totalWithoutTax.value)), 1 /* TEXT */)
                          ]),
                          (!isTaxMode.value)
                            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(taxRows.value, (vatAmount) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: vatAmount.id,
                                  class: "ocr-form__vat-amount"
                                }, [
                                  _createElementVNode("span", null, _toDisplayString(`${vatAmount.rate}%`), 1 /* TEXT */),
                                  _createElementVNode("span", null, _toDisplayString(formatAmount(
                                                                getTotalByTax(
                                                                    vatAmount.id,
                                                                    vatAmount.rate,
                                                                ),
                                                            )), 1 /* TEXT */)
                                ]))
                              }), 128 /* KEYED_FRAGMENT */))
                            : _createCommentVNode("v-if", true),
                          _createElementVNode("div", _hoisted_25, [
                            _createElementVNode("span", null, _toDisplayString(_unref($t).__('ocr.OcrVerificationForm.total_tax')), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_26, _toDisplayString(formatAmount(totalTax.value)), 1 /* TEXT */)
                          ]),
                          _createElementVNode("div", _hoisted_27, [
                            _createElementVNode("span", null, _toDisplayString(_unref($t).__('common.labels.amount_including_tax')), 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_28, _toDisplayString(formatAmount(totalWithTax.value)), 1 /* TEXT */)
                          ])
                        ])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["title", "type"]),
                    (canAddPayment.value)
                      ? (_openBlock(), _createBlock(_component_CompassAccordion, {
                          key: 0,
                          ref_key: "paymentsSectionRef",
                          ref: paymentsSectionRef,
                          class: "ocr-form__card-section ocr-form__content-payment",
                          title: 
                                        _unref($t).__(
                                            isCreditNote.value
                                                ? 'ocr.OcrVerificationForm.refund_title'
                                                : 'ocr.OcrVerificationForm.payment_title',
                                        )
                                    ,
                          type: _ctx.CompassEnums.AccordionType.FUNCTIONAL,
                          coloredIconProps: { icon: 'pig-money', color: 'orange' },
                          open: ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_29, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(form).payments, (payment, index) => {
                                return (_openBlock(), _createElementBlock("div", { key: index }, [
                                  _createElementVNode("div", _hoisted_30, [
                                    _createElementVNode("h3", _hoisted_31, _toDisplayString(_unref($t).__(
                                                            isCreditNote.value
                                                                ? 'ocr.OcrVerificationForm.refund_item_title'
                                                                : 'ocr.OcrVerificationForm.payment_item_title',
                                                            { number: index + 1 },
                                                        )), 1 /* TEXT */),
                                    _createVNode(_component_CompassIconButton, {
                                      icon: "bin",
                                      disabled: submitting.value,
                                      variant: 
                                                        _ctx.CompassEnums.IconButtonVariant.DESTRUCTIVE
                                                    ,
                                      size: _ctx.CompassEnums.IconButtonSize.MEDIUM,
                                      onClick: ($event: any) => (deletePayment(index))
                                    }, null, 8 /* PROPS */, ["disabled", "variant", "size", "onClick"])
                                  ]),
                                  _createElementVNode("div", _hoisted_32, [
                                    _createVNode(_component_CompassFormItem, {
                                      label: _unref($t).__('common.labels.date'),
                                      required: _unref(isPaymentPartiallyFilled)(payment),
                                      error: getPaymentError(index, 'date'),
                                      class: "ocr-form__form-item--date"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_CompassDatePicker, {
                                          modelValue: payment.date,
                                          disabled: submitting.value,
                                          formatDateDisplayed: _unref(formatDateDisplayed),
                                          error: !!getPaymentError(index, 'date'),
                                          "onUpdate:modelValue": 
                                                            (value) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'date',
                                                                    value,
                                                                )
                                                        
                                        }, null, 8 /* PROPS */, ["modelValue", "disabled", "formatDateDisplayed", "error", "onUpdate:modelValue"])
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "required", "error"]),
                                    _createVNode(_component_CompassFormItem, {
                                      label: _unref($t).__('common.labels.reference'),
                                      class: "ocr-form__form-item--payment-reference"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_CompassInput, {
                                          modelValue: payment.reference,
                                          disabled: submitting.value,
                                          "onUpdate:modelValue": 
                                                            (value) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'reference',
                                                                    value,
                                                                )
                                                        
                                        }, null, 8 /* PROPS */, ["modelValue", "disabled", "onUpdate:modelValue"])
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label"]),
                                    _createVNode(_component_CompassFormItem, {
                                      label: _unref($t).__('common.labels.amount'),
                                      required: _unref(isPaymentPartiallyFilled)(payment),
                                      error: getPaymentError(index, 'amount'),
                                      class: "ocr-form__form-item--payment-amount"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_CompassInput, {
                                          modelValue: payment.amount,
                                          disabled: submitting.value,
                                          inputmode: "decimal",
                                          error: !!getPaymentError(index, 'amount'),
                                          "onUpdate:modelValue": 
                                                            (value) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'amount',
                                                                    _unref(limitToDecimals)(
                                                                        value,
                                                                        _unref(DEFAULT_AMOUNT_DECIMALS),
                                                                    ),
                                                                )
                                                        ,
                                          onBlur: ($event: any) => (formatPaymentAmountOnBlur(index))
                                        }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                                          (currencySymbol.value)
                                            ? {
                                                name: "suffix",
                                                fn: _withCtx(() => [
                                                  _createTextVNode(_toDisplayString(currencySymbol.value), 1 /* TEXT */)
                                                ]),
                                                key: "0"
                                              }
                                            : undefined
                                        ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled", "error", "onUpdate:modelValue", "onBlur"])
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "required", "error"]),
                                    _createVNode(_component_CompassFormItem, {
                                      label: 
                                                        _unref($t).__(
                                                            isCreditNote.value
                                                                ? 'common.labels.refund_method'
                                                                : 'common.labels.payment_method',
                                                        )
                                                    ,
                                      required: _unref(isPaymentPartiallyFilled)(payment),
                                      error: getPaymentError(index, 'payment_mean'),
                                      class: "ocr-form__form-item--payment-method"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_CompassSelect, {
                                          modelValue: payment.payment_mean,
                                          disabled: submitting.value,
                                          filterable: "",
                                          error: 
                                                            !!getPaymentError(index, 'payment_mean')
                                                        ,
                                          placeholder: 
                                                            _unref($t).__(
                                                                isCreditNote.value
                                                                    ? 'ocr.OcrVerificationForm.select_refund_method'
                                                                    : 'ocr.OcrVerificationForm.select_payment_method',
                                                            )
                                                        ,
                                          "onUpdate:modelValue": 
                                                            (value) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'payment_mean',
                                                                    value,
                                                                )
                                                        
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_CompassDropdownOption, {
                                              value: undefined,
                                              label: 
                                                                _unref($t).__('common.labels.none_option')
                                                            
                                            }, null, 8 /* PROPS */, ["label"]),
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(paymentTypes), (item) => {
                                              return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                                                key: item.id,
                                                label: item.label,
                                                value: item.id
                                              }, null, 8 /* PROPS */, ["label", "value"]))
                                            }), 128 /* KEYED_FRAGMENT */))
                                          ]),
                                          _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["modelValue", "disabled", "error", "placeholder", "onUpdate:modelValue"])
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "required", "error"])
                                  ])
                                ]))
                              }), 128 /* KEYED_FRAGMENT */)),
                              _createVNode(_component_CompassButton, {
                                label: 
                                                _unref($t).__(
                                                    isCreditNote.value
                                                        ? 'ocr.OcrVerificationForm.add_refund'
                                                        : 'ocr.OcrVerificationForm.add_payment',
                                                )
                                            ,
                                type: _ctx.CompassEnums.ButtonType.TERTIARY,
                                disabled: submitting.value,
                                leftIcon: "plus",
                                onClick: addPayment
                              }, null, 8 /* PROPS */, ["label", "type", "disabled"])
                            ])
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["title", "type"]))
                      : _createCommentVNode("v-if", true)
                  ], 512 /* NEED_PATCH */), [
                    [_vShow, !processing.value]
                  ])
                ]))
          ]),
          _: 1 /* STABLE */
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_33, [
      _createElementVNode("div", _hoisted_34, [
        _createVNode(_component_CompassButton, {
          label: 
                        isElectronic.value
                            ? _unref($t).__('common.actions.refuse')
                            : _unref($t).__('common.actions.delete')
                    ,
          type: _ctx.CompassEnums.ButtonType.TERTIARY_DESTRUCTIVE,
          disabled: submitting.value || isElectronic.value,
          "data-bot": "purchases-purinvoices-verify_delete",
          onClick: deleteOCR
        }, null, 8 /* PROPS */, ["label", "type", "disabled"]),
        _createVNode(_component_CompassButton, {
          label: _unref($t).__('common.actions.ignore'),
          type: _ctx.CompassEnums.ButtonType.SECONDARY,
          disabled: submitting.value,
          "data-bot": "purchases-purinvoices-verify_ignore",
          onClick: skip
        }, null, 8 /* PROPS */, ["label", "type", "disabled"]),
        _createVNode(_component_CompassButton, {
          nativeType: "button",
          label: 
                        isElectronic.value
                            ? _unref($t).__('common.actions.approve')
                            : _unref($t).__('common.actions.validate')
                    ,
          type: _ctx.CompassEnums.ButtonType.PRIMARY,
          loading: submitting.value,
          "data-bot": "purchases-purinvoices-verify_validate",
          onClick: _unref(submit)
        }, null, 8 /* PROPS */, ["label", "type", "loading", "onClick"])
      ])
    ]),
    _createVNode(DeleteOcrProductLineModal, {
      visible: productDeleteModalVisible.value,
      rememberChoice: productDeleteRememberChoice.value,
      "onUpdate:visible": _cache[7] || (_cache[7] = ($event: any) => (productDeleteModalVisible.value = $event)),
      "onUpdate:rememberChoice": _cache[8] || (_cache[8] = ($event: any) => (productDeleteRememberChoice.value = $event)),
      onConfirm: onProductDeleteConfirm,
      onCancel: onProductDeleteCancel
    }, null, 8 /* PROPS */, ["visible", "rememberChoice"]),
    _createVNode(OcrAmountDifferenceModal, {
      visible: amountDifferenceModalVisible.value,
      "onUpdate:visible": _cache[9] || (_cache[9] = ($event: any) => ((amountDifferenceModalVisible).value = $event)),
      lineTotal: formatAmount(totalWithTax.value),
      ocrTotal: formatAmount(ocrTotalWithTax.value),
      isTaxMode: isTaxMode.value,
      onConfirm: confirmAmountDifference,
      onCancel: cancelAmountDifference
    }, null, 8 /* PROPS */, ["visible", "lineTotal", "ocrTotal", "isTaxMode"])
  ]))
}
}

})